import { CSSProperties, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { HoverCardExternalTriggerClass, HoverCardProps } from './hover-card.types';

import styles from './hover-card.module.css';

const HoverCard = ({
  aspectRatio,
  backgroundColor = 'transparent',
  backgroundImg,
  children,
  containerClassName,
  hoverOnTab = true,
  height,
  width,
  type,
  duration,
  delay,
  preHoverContentSlide,
  disableHover,
  hasExternalTrigger,
  forceHoverState,
  borderRadius,
}: HoverCardProps) => {
  const animationType = type || 'fade';
  const animationDuration = duration ?? 250;
  const animationDelay = delay ?? 0;

  const containerRef = useRef<HTMLDivElement>(null);

  // prevent the card from staying open when the user clicks on it and then moves the mouse out
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleMouseLeave = () => {
      el.blur();
    };
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      tabIndex={hoverOnTab ? 0 : -1}
      className={classNames(styles.card, containerClassName, styles[animationType], {
        [styles.hoverOnTab]: hoverOnTab,
        [styles.preHoverContentSlide]: preHoverContentSlide,
        [styles.disableHover]: disableHover,
        [styles[HoverCardExternalTriggerClass]]: !!hasExternalTrigger,
        [styles.forceHoverState]: forceHoverState,
      })}
      data-component="HoverCard"
      style={
        {
          ...(height ? { '--hover-card-height': height } : {}),
          ...(width ? { '--hover-card-width': width } : {}),
          ...(aspectRatio ? { '--hover-card-aspect-ratio': aspectRatio } : {}),
          ...(backgroundColor && { '--hover-card-background-color': backgroundColor }),
          ...(backgroundImg && { '--hover-card-background-img': `url(${backgroundImg})` }),
          ...(borderRadius && { '--hover-card-radius': borderRadius }),
          '--hover-card-duration': `${animationDuration}ms`,
          '--hover-card-delay': `${animationDelay}ms`,
        } as CSSProperties
      }
    >
      <div className={`height-100-percent width-100-percent ${styles.cardContent}`}>
        <>{children}</>
      </div>
    </div>
  );
};

export default HoverCard;
